// DonationSurvey utilities - extracted from presentation layer
// All utility functions, constants, and type definitions for DonationSurvey

import * as Api from '@ViewModels';
import * as yup from 'yup';
import { createBaseSurveySchema, createCustomFormFieldSchema } from '../shared/validation/schemas';

// Critical constants used across DonationSurvey components
export const QUESTION_LIMIT = 20;
export const MAX_SUGGESTED_AMOUNTS = 10;
export const MIN_DONATION_AMOUNT = 1;
export const MAX_DONATION_AMOUNT = 100000;
export const DEFAULT_SUGGESTED_AMOUNTS = [25, 50, 100, 250, 500];
export const DEFAULT_MINIMUM_AMOUNT = 1;

// Tab values for consistency with other survey types
export const TabValues = {
	Questions: 'Questions',
	Settings: 'Settings',
	Preview: 'Preview',
} as const;

export type TabValues = (typeof TabValues)[keyof typeof TabValues];

// Form type for DonationSurvey (following other survey patterns)
// Omits server-generated fields that aren't needed during form editing
export type DonationSurveyFormData = Omit<
	Api.IDonationSurvey,
	'_type' | 'id' | 'anonymousLink' | 'archivedDate' | 'stats'
> & {
	donationPageTitle: string;
	causeDetails: string;
	allowProgramSpecificDonations: boolean;
	programSpecificDonation: Api.IDonationCampaign[];
	hasExpirationDate: boolean;
	image: Api.IFileAttachment | null;
	imageSource: Api.IAttachmentUploadSource | null;
	collectDonorPhone: DonationDonorInfoCollection;
	collectDonorAddress: DonationDonorInfoCollection;
	allowMonthlyDonations: boolean;
	hasDonationValues: boolean;
	donationValues: { value: number }[];
	allowCustomDonationAmount: boolean;
	hasMinimumDonationAmount: boolean;
	minimumDonationAmount: number;
	allowCustomerToCoverServiceFees: boolean;
	serviceFeeCalculationMethod: DonationServiceFeeCalculationMethod;
	serviceFeeFixedAmount: number;
	serviceFeePercentage: number;
};

export enum DonationDonorInfoCollection {
	COLLECT = 'collect',
	REQUIRE = 'require',
	NO_COLLECT = 'doNotCollect',
}

export enum DonationServiceFeeCalculationMethod {
	STRIPE = 'Stripe',
	FIXED = 'Fixed',
	PERCENTAGE = 'Percentage',
}

// Validation schema for DonationSurvey using shared schemas
export const validationSchema = yup.object({
	// Base survey fields using shared schema
	...createBaseSurveySchema({
		requireIntro: false,
		requireStartDate: false,
		requireExpirationDate: true,
	}),
	donationPageTitle: yup.string().required('Donation page title is required'),
	causeDetails: yup.string().required('Cause details is required'),
	allowProgramSpecificDonations: yup.boolean().required('Allow program specific donations is required'),
	hasExpirationDate: yup.boolean().required('Has expiration date is required'),
	collectDonorPhone: yup.string().required('Collect donor phone is required'),
	collectDonorAddress: yup.string().required('Collect donor address is required'),
	allowMonthlyDonations: yup.boolean().required('Allow monthly donations is required'),
	hasDonationValues: yup.boolean().required('Has donation values is required'),
	donationValues: yup
		.array()
		.of(
			yup.object().shape({
				value: yup.number().positive('Donation value must be positive').required('Donation value is required'),
			})
		)
		.required('Donation values are required'),
	allowCustomDonationAmount: yup.boolean().required('Allow custom donation amount is required'),
	hasMinimumDonationAmount: yup.boolean().required('Has minimum donation amount is required'),
	minimumDonationAmount: yup.number().when('hasMinimumDonationAmount', {
		is: true,
		then: schema =>
			schema.required('Minimum donation amount is required').positive('Minimum donation amount must be positive'),
		otherwise: schema => schema.nullable(),
	}),
	allowCustomerToCoverServiceFees: yup.boolean().required('Allow customer to cover service fees is required'),
	serviceFeeCalculationMethod: yup.string().required('Service fee calculation method is required'),
	serviceFeeFixedAmount: yup.number().required('Service fee fixed amount is required'),
	serviceFeePercentage: yup.number().required('Service fee percentage is required'),

	customForm: yup.object({
		name: yup.string(),
		startDate: yup.date(),
		fields: createCustomFormFieldSchema({
			requireLabels: false, // CustomSurvey allows flexible label requirements
		}),
	}),
});
